import React, { useRef, useState } from 'react';

import { Splide, SplideSlide } from '@splidejs/react-splide';
import type { Options } from '@splidejs/splide';

import { ProductTile } from '@src/stories/ecommerce/Product/ProductTile';
import { ObviyoProductRecommendations } from '@src/stories/blocks/ObviyoRecommendations/ObviyoProductRecommendations';
import CarouselArrows from '@src/stories/elements/CarouselArrows/CarouselArrows';

import { getGlobalBreakpoints } from '@src/hooks/useMediaQuery';
import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';

interface PopularProductsProps {
  title?: string;
  products?: any[];
  className?: string;
  onViewMore?: () => void;
}

export const PopularProducts: React.FC<PopularProductsProps> = ({
  title = 'Popular products',
  products = [],
  settings,
  className = '',
  onViewMore
}) => {
  const {
    recommendation_id, 
    tile_type = 'default',
    layout_type = 'carousel',
    layout_type_mobile = 'carousel',
    layout_type_tablet = 'carousel',
    layout_type_ultrawide = 'carousel',
    show_carousel_arrows = true,
    columns_vertical_scroll = 2,
    columns_vertical_scroll_mobile = 1,
    columns_vertical_scroll_tablet = 3,
    columns_vertical_scroll_ultrawide = 4
  } = settings.settings.block_settings;

  const { isMobile, isTablet, isUltrawide } = useResponsiveBreakpoints();
  const globalBreakpoints = getGlobalBreakpoints();
  const { mobile: mobile_max, tablet: tablet_max, desktop: desktop_max } = globalBreakpoints;

  const [hasRecommendations, setHasRecommendations] = useState(false);

  const splideRef = useRef<any>(null);
  const fallbackSplideRef = useRef<{ splide: any } | null>(null);

  const displayProducts = products.length > 0 ? products : [];

  const handlePrevious = () => {
    if (splideRef.current) {
      splideRef.current.splide?.go('<');
    }
  };

  const handleNext = () => {
    if (splideRef.current) {
      splideRef.current.splide?.go('>');
    }
  };

  const splideOptions: Options = {
    type: 'slide',
    perPage: 4,
    padding: {
      right: 100
    },
    perMove: 1,
    gap: 16,
    arrows: false,
    pagination: false,
    breakpoints: {
      [tablet_max]: {
        perPage: 3,
        padding: {
          right: 100
        }
      },
      [mobile_max]: {
        perPage: 2,
        gap: 8,
        padding: {
          right: 0
        }
      }
    }
  };

  const obviyoSettings = {
    ...settings,
    slides_per_page_mobile: 2,
    slide_gap_mobile: 8,
    carousel_padding_right_mobile: 0,
    slides_per_page_tablet: 3,
    slide_gap_tablet: 16,
    carousel_padding_right_tablet: 100,
    slides_per_page: 4,
    slide_gap: 16,
    carousel_padding_right: 100
  };

  // Render horizontal scroll layout
  const renderHorizontalScroll = () => {    
    return (
      <div className="w-full overflow-x-auto overflow-y-hidden">
        <div className="scrollbar-thin scrollbar-track-transparent pb-4 min-w-0">
          <div className="flex gap-4">
            {displayProducts.map((product, index) => (
              <div key={index} className="flex-shrink-0 min-w-0">
                <ProductTile
                  product={product}
                  settings={settings}
                  siblings={[]}
                  globals={{}}
                  enableWishlist={true}
                  showTags={true}
                  type={tile_type}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render vertical scroll layout with columns
  const renderVerticalScroll = () => {
    let columns;
    if (isMobile) {
      columns = columns_vertical_scroll_mobile;
    } else if (isTablet) {
      columns = columns_vertical_scroll_tablet;
    } else if (isUltrawide) {
      columns = columns_vertical_scroll_ultrawide;
    } else {
      columns = columns_vertical_scroll;
    }
    
    const gridColsClass = `grid-cols-${columns}`;
    
    return (
      <div className="overflow-y-auto max-h-[515px] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
        <div className={`grid ${gridColsClass} gap-x-4 gap-y-6 pr-2`}>
          {displayProducts.map((product, index) => (
            <div key={index} className="w-full">
              <ProductTile
                product={product}
                settings={settings}
                siblings={[]}
                globals={{}}
                enableWishlist={true}
                showTags={true}
                type={tile_type}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render grid layout (for vertical scroll alternative)
  const renderGrid = () => {
    const gridCols = isMobile ? 'grid-cols-2' : 'grid-cols-4';
    return (
      <div className={`grid ${gridCols} gap-4`}>
        {displayProducts.map((product, index) => (
          <div key={index} className="w-full">
            <ProductTile
              product={product}
              settings={settings}
              siblings={[]}
              globals={{}}
              enableWishlist={true}
              showTags={true}
              type={tile_type}
            />
          </div>
        ))}
      </div>
    );
  };

  // Render carousel layout
  const renderCarousel = () => (
    <>
      {recommendation_id && (
        <ObviyoProductRecommendations
          settings={obviyoSettings}
          ref={splideRef}
          onRecommendationsLoaded={(loaded) => setHasRecommendations(loaded)}
        />
      )}

      {!hasRecommendations && (
        <Splide
          options={splideOptions}
          onMounted={(splide) => {
            fallbackSplideRef.current = { splide };
          }}
        >
          {displayProducts.map((product, index) => (
            <SplideSlide key={index}>
              <div className='w-full'>
                <ProductTile
                  product={product}
                  settings={settings}
                  siblings={[]}
                  globals={{}}
                  enableWishlist={true}
                  showTags={true}
                  type={tile_type}
                />
              </div>
            </SplideSlide>
          ))}
        </Splide>
      )}
    </>
  );

  // Determine which layout to use based on device
  let currentLayoutType;
  if (isMobile) {
    currentLayoutType = layout_type_mobile;
  } else if (isTablet) {
    currentLayoutType = layout_type_tablet;
  } else if (isUltrawide) {
    currentLayoutType = layout_type_ultrawide;
  } else {
    currentLayoutType = layout_type;
  }
  
  const shouldShowArrows = currentLayoutType === 'carousel' && show_carousel_arrows;

  return (
    <div className={`w-full min-w-0 ${className}`}>
      <div className={`flex flex-row items-center justify-between w-full min-w-0 ${isMobile || isTablet ? 'mb-3' : 'mb-[60px]'}`}>
        <p className={`${isMobile || isTablet ? 'b1--bold' : 'h1'} flex-shrink mr-4 min-w-0`}>{title}</p>
        {shouldShowArrows && (
          <div className='flex justify-end items-center space-x-2 flex-shrink-0'>
            <CarouselArrows
              splideRef={hasRecommendations ? splideRef : fallbackSplideRef}
              buttonClasses='text-[24px]'
            />
          </div>
        )}
      </div>

      {currentLayoutType === 'horizontal_scroll' && renderHorizontalScroll()}
      {currentLayoutType === 'vertical_scroll' && renderVerticalScroll()}
      {currentLayoutType === 'carousel' && renderCarousel()}
    </div>
  );
};
